'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  alt: string;
  category: string;
  available: boolean;
}

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product, quantity: number) => void;
}

export default function ProductCard({ product, onAddToCart }: ProductCardProps) {
  const [quantity, setQuantity] = useState(1);

  const handleIncrement = () => setQuantity((prev) => prev + 1);
  const handleDecrement = () => setQuantity((prev) => (prev > 1 ? prev - 1 : 1));

  const handleAddToCart = () => {
    onAddToCart(product, quantity);
    setQuantity(1);
  };

  return (
    <div className="glass-card rounded-2xl overflow-hidden group hover:shadow-xl transition-all duration-500">
      {/* Product Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <AppImage
          src={product.image}
          alt={product.alt}
          className={`w-full h-full object-cover transition-all duration-700 ${
            product.available ? 'grayscale-hover' : 'grayscale opacity-50'
          }`}
        />
        {!product.available && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <span className="text-white font-bold text-lg uppercase tracking-wider">
              Sold Out
            </span>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-foreground mb-2 font-heading">
          {product.name}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
          {product.description}
        </p>

        {/* Price and Actions */}
        <div className="flex items-center justify-between">
          <span className="text-2xl font-black text-primary">
            ${product.price.toFixed(2)}
          </span>

          {product.available && (
            <div className="flex items-center gap-3">
              {/* Quantity Stepper */}
              <div className="flex items-center gap-2 bg-accent rounded-full px-3 py-1.5">
                <button
                  onClick={handleDecrement}
                  className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-primary hover:text-primary-foreground transition-colors"
                  aria-label="Decrease quantity"
                >
                  <Icon name="MinusIcon" size={14} />
                </button>
                <span className="text-sm font-bold w-8 text-center">{quantity}</span>
                <button
                  onClick={handleIncrement}
                  className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-primary hover:text-primary-foreground transition-colors"
                  aria-label="Increase quantity"
                >
                  <Icon name="PlusIcon" size={14} />
                </button>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                className="bg-secondary text-secondary-foreground px-4 py-2 rounded-full hover:bg-secondary/90 transition-all hover:scale-105 shadow-lg flex items-center gap-2"
                aria-label={`Add ${product.name} to cart`}
              >
                <Icon name="ShoppingCartIcon" size={16} />
                <span className="text-xs font-bold uppercase">Add</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}